<template>
  <div class="maintenance-container">
    <div class="maintenance-card">
      <div class="card-header">
        <div class="header-content">
          <i class="fas fa-broom text-4xl text-orange-500 mb-3"></i>
          <h3 class="text-2xl font-bold text-gray-800">Maintenance des Logs</h3>
          <p class="text-gray-600 mt-2">
            Nettoyez les anciens logs pour libérer de l'espace dans la base de données
          </p>
        </div>
      </div>

      <div class="card-body">
        <div class="warning-box">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <div>
            <p class="font-semibold">Attention !</p>
            <p class="text-sm mt-1">
              Cette action est irréversible. Les logs supprimés ne pourront pas être récupérés.
            </p>
          </div>
        </div>

        <div class="cleanup-options">
          <h4 class="text-lg font-semibold text-gray-800 mb-4">
            Supprimer les logs plus anciens que :
          </h4>

          <div class="options-grid">
            <button
              v-for="option in cleanupOptions"
              :key="option.days"
              @click="selectedDays = option.days"
              :class="[
                'option-card',
                selectedDays === option.days ? 'selected' : ''
              ]"
            >
              <i :class="option.icon + ' text-3xl mb-2'"></i>
              <p class="font-semibold text-lg">{{ option.days }} jours</p>
              <p class="text-sm text-gray-600 mt-1">{{ option.label }}</p>
            </button>
          </div>

          <div class="custom-days">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Ou spécifiez un nombre de jours personnalisé :
            </label>
            <input
              v-model.number="customDays"
              type="number"
              min="1"
              placeholder="Ex: 90"
              class="days-input"
              @input="selectedDays = customDays"
            />
          </div>
        </div>

        <div v-if="selectedDays" class="confirmation-section">
          <div class="confirmation-box">
            <i class="fas fa-info-circle mr-2"></i>
            <p>
              Vous êtes sur le point de supprimer tous les logs de plus de 
              <strong>{{ selectedDays }} jours</strong>.
            </p>
          </div>

          <div class="action-buttons">
            <button
              @click="confirmCleanup"
              :disabled="loading"
              class="btn btn-danger"
            >
              <i class="fas fa-trash mr-2"></i>
              {{ loading ? 'Suppression en cours...' : 'Confirmer la suppression' }}
            </button>
            <button
              @click="resetSelection"
              :disabled="loading"
              class="btn btn-secondary"
            >
              <i class="fas fa-times mr-2"></i>
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistiques de nettoyage -->
    <div class="stats-card">
      <h4 class="text-lg font-semibold text-gray-800 mb-4">
        <i class="fas fa-chart-pie mr-2"></i>Informations de stockage
      </h4>
      
      <div class="storage-info">
        <div class="info-item">
          <i class="fas fa-database text-blue-500"></i>
          <div>
            <p class="text-sm text-gray-600">Total des logs</p>
            <p class="text-xl font-bold text-gray-800">{{ formatNumber(totalLogs) }}</p>
          </div>
        </div>

        <div class="info-item">
          <i class="fas fa-calendar-alt text-green-500"></i>
          <div>
            <p class="text-sm text-gray-600">Dernier nettoyage</p>
            <p class="text-xl font-bold text-gray-800">
              {{ lastCleanup || 'Jamais' }}
            </p>
          </div>
        </div>
      </div>

      <div class="tips-section">
        <h5 class="font-semibold text-gray-800 mb-2">
          <i class="fas fa-lightbulb mr-2 text-yellow-500"></i>Recommandations
        </h5>
        <ul class="tips-list">
          <li>
            <i class="fas fa-check text-green-500 mr-2"></i>
            Effectuez un nettoyage mensuel pour des performances optimales
          </li>
          <li>
            <i class="fas fa-check text-green-500 mr-2"></i>
            Conservez au minimum 30 jours de logs pour l'analyse
          </li>
          <li>
            <i class="fas fa-check text-green-500 mr-2"></i>
            Exportez les logs importants avant suppression
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LogsMaintenance',
  emits: ['cleanup'],
  data() {
    return {
      selectedDays: null,
      customDays: null,
      loading: false,
      totalLogs: 15234, // Cette valeur devrait venir de l'API
      lastCleanup: null, // Cette valeur devrait venir de l'API
      cleanupOptions: [
        { days: 7, label: '1 semaine', icon: 'fas fa-calendar-day' },
        { days: 30, label: '1 mois', icon: 'fas fa-calendar-week' },
        { days: 90, label: '3 mois', icon: 'fas fa-calendar' },
        { days: 180, label: '6 mois', icon: 'fas fa-calendar-alt' },
      ],
    };
  },
  methods: {
    confirmCleanup() {
      if (!this.selectedDays) return;
      
      const confirmed = confirm(
        `Êtes-vous sûr de vouloir supprimer tous les logs de plus de ${this.selectedDays} jours ?\n\nCette action est irréversible.`
      );

      if (confirmed) {
        this.loading = true;
        this.$emit('cleanup', this.selectedDays);
        
        // Simuler un délai
        setTimeout(() => {
          this.loading = false;
          this.resetSelection();
        }, 2000);
      }
    },
    resetSelection() {
      this.selectedDays = null;
      this.customDays = null;
    },
    formatNumber(num) {
      return new Intl.NumberFormat('fr-FR').format(num || 0);
    },
  },
};
</script>

<style scoped>
.maintenance-container {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
}

@media (max-width: 1024px) {
  .maintenance-container {
    grid-template-columns: 1fr;
  }
}

.maintenance-card,
.stats-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: white;
  padding: 2rem;
  text-align: center;
}

.card-body {
  padding: 2rem;
}

.warning-box {
  background-color: #fef3c7;
  border-left: 4px solid #f59e0b;
  padding: 1rem;
  border-radius: 6px;
  display: flex;
  align-items: start;
  gap: 0.75rem;
  margin-bottom: 2rem;
  color: #92400e;
}

.cleanup-options {
  margin-bottom: 2rem;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.option-card {
  padding: 1.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.option-card:hover {
  border-color: #16a34a;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.option-card.selected {
  border-color: #16a34a;
  background-color: #dcfce7;
}

.option-card i {
  color: #6b7280;
  transition: color 0.2s;
}

.option-card.selected i {
  color: #16a34a;
}

.custom-days {
  margin-top: 1.5rem;
}

.days-input {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.days-input:focus {
  outline: none;
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.1);
}

.confirmation-section {
  border-top: 1px solid #e5e7eb;
  padding-top: 2rem;
}

.confirmation-box {
  background-color: #dbeafe;
  border-left: 4px solid #2563eb;
  padding: 1rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  color: #1e40af;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-danger {
  background-color: #dc2626;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(220, 38, 38, 0.3);
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #4b5563;
}

.stats-card {
  padding: 2rem;
}

.storage-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 8px;
}

.info-item i {
  font-size: 2rem;
}

.tips-section {
  background-color: #fef3c7;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1.5rem;
}

.tips-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tips-list li {
  padding: 0.5rem 0;
  font-size: 0.875rem;
  color: #374151;
  display: flex;
  align-items: start;
}
</style>

